import { useEffect, useState } from 'react';
import { Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  COLOR_PALETTE,
  setAreaColor,
  listKnownAreas,
  getAllAreasMerged,
} from '../../lib/areaColors';

/**
 * Pannello in Impostazioni per personalizzare i colori delle aree.
 *
 * - Lista delle aree conosciute (default + eventuali custom)
 * - Per ogni area, click sul pallino corrente → apre palette con 12 colori
 * - Selezione: salva su localStorage e applica subito ai badge in questa stessa UI
 * - Bottone "Aggiorna le viste": ricarica la pagina così tutto si rinfresca
 *
 * Le modifiche sono per-device (localStorage). Vengono incluse nei backup
 * solo se in futuro estendiamo l'export — per ora restano locali.
 */
export default function AreaColorPicker() {
  const [, force] = useState(0);
  const [openKey, setOpenKey] = useState(null);

  // re-render quando arriva un cambio
  useEffect(() => {
    const onChange = () => force((n) => n + 1);
    window.addEventListener('qp:areas-changed', onChange);
    return () => window.removeEventListener('qp:areas-changed', onChange);
  }, []);

  const areas = listKnownAreas();
  const meta = getAllAreasMerged();

  const pick = async (key, color) => {
    const res = await setAreaColor(key, color);
    setOpenKey(null);
    if (res?.ok) {
      toast.success(`Colore aggiornato per ${meta[key].label}`);
    } else {
      toast.error(res?.error || 'Errore aggiornamento colore');
    }
  };

  return (
    <div className="py-4 border-b border-linesoft" data-testid="area-colors-section">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="font-display text-base mb-1">Colori aree</div>
          <p className="font-body text-sm text-soft leading-relaxed max-w-md">
            Personalizza il colore di ogni area. I colori sono usati ovunque nell&apos;app
            (badge, target, divisori). Le scelte sono salvate su questo dispositivo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-line text-soft hover:border-ink hover:text-ink font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
          data-testid="area-colors-reload-btn"
          title="Ricarica per applicare ovunque"
        >
          <RefreshCw size={12} strokeWidth={1.5} /> Aggiorna viste
        </button>
      </div>

      <ul className="space-y-2 mt-4">
        {areas.map((key) => {
          const m = meta[key];
          if (!m) return null;
          const isOpen = openKey === key;
          return (
            <li key={key} className="flex items-center gap-3" data-testid={`area-row-${key}`}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : key)}
                className="w-7 h-7 border border-line hover:border-ink shrink-0"
                style={{ backgroundColor: m.color }}
                aria-label={`Cambia colore ${m.label}`}
                data-testid={`area-swatch-${key}`}
              />
              <span className="font-body text-sm text-ink min-w-[100px]">{m.label}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                {m.color.toUpperCase()}
              </span>
              {isOpen && (
                <div
                  className="flex items-center gap-1.5 ml-3 p-2 border border-ink bg-canvas"
                  data-testid={`area-palette-${key}`}
                >
                  {COLOR_PALETTE.map((c) => {
                    const active = c.toLowerCase() === (m.color || '').toLowerCase();
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => pick(key, c)}
                        className={
                          'w-6 h-6 rounded-full border transition-transform hover:scale-110 ' +
                          (active ? 'border-ink ring-2 ring-ink ring-offset-1' : 'border-line')
                        }
                        style={{ backgroundColor: c }}
                        aria-label={`Colore ${c}`}
                        data-testid={`area-color-${key}-${c}`}
                      >
                        {active && <Check size={12} strokeWidth={2} className="text-white mx-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
